import ContextMenu from './ContextMenu';
import Utils from './Utils';
import Draggable from './Draggable.js';

class CollectionView {
  constructor(domElement, options = {}) {
    this.options = Object.assign({
      checkboxes: true,
      draggable: true,
    }, options);

    if (!CollectionView.instanceMap) {
      CollectionView.instanceMap = new Map();
    }

    CollectionView.instanceMap.set(domElement, this);

    this.domElement = domElement;
    this.headerDomElement = this.findOrCreateHeader();

    if (!!this.headerDomElement) {
      this.headerCheckboxDomElement = this.headerDomElement.getElementsByClassName(
        'checkbox-control')[0];
      this.headerActionsDomElement = this.headerDomElement.getElementsByClassName(
        'table-column-actions')[0];
    }

    this.bodyElement = this.domElement.parentNode.querySelector(
      '.table-body');
    this.itemDomElements = this.domElement.parentNode.querySelectorAll(
      '.table-body .table-row');
    this.actionButtonDomElements = this.bodyElement.getElementsByClassName(
      'table-column-actions');

    this.primaryKeyFieldName = 'pk-undefined';
    this.headerAutoFieldsMode = true;
    this.headerFields = [];
    this.hiddenFieldPatterns = [];
    this.renderFunctions = {};
    this.actions = [];

    this.bindEvents();
  }

  findOrCreateHeader() {
    const headerDomElement = this.domElement.getElementsByClassName('table-header')[0];
    if (!headerDomElement) {
      return null;
    }

    const headerRowDomElements = headerDomElement.getElementsByClassName('table-row');
    if (headerRowDomElements.length > 0) {
      return headerRowDomElements[0];
    }

    const headerRowDomElement = document.createElement('tr');
    headerRowDomElement.className = 'table-row';

    if (this.options.draggable) {
      this.findOrCreateDragHandle(headerRowDomElement, true);
    }

    if (this.options.checkboxes) {
      this.findOrCreateCheckboxControl(headerRowDomElement, true);
    }

    this.headerActionsDomElement = this.findOrCreateActionsHeaderColumn(headerRowDomElement, true);

    return headerDomElement.appendChild(headerRowDomElement);
  }

  recreateActionElements() {
    if (!!this.headerActionsDomElement) {
      this.headerActionsDomElement.innerHTML = '';
    }

    for (let i = 0; i < this.actions.length; i++) {
      const action = this.actions[i];

      if (action.scope === 'item') {
        continue;
      }

      const actionDomElement = document.createElement('a');
      actionDomElement.className = action.className || '';
      actionDomElement.href = 'javascript://';

      const actionIconDomElement = document.createElement('i');
      actionIconDomElement.className = action.icon;
      actionDomElement.appendChild(actionIconDomElement);

      if (typeof (action.cb) === 'function') {
        actionDomElement.addEventListener('click', function(e) {
          e.preventDefault();
          action.cb(e);
        });
      }

      if (!!this.headerActionsDomElement) {
        if (action.scope === 'bulk' &&
          this.headerActionsDomElement.childNodes.length > 0) {
          this.headerActionsDomElement.insertBefore(actionDomElement,
            this.headerActionsDomElement.childNodes[0]);
        } else {
          this.headerActionsDomElement.appendChild(actionDomElement);
        }
      }
    }
  }

  addAction({
    name,
    icon,
    label = '',
    className = '',
    scope = 'main', // "main" | "bulk" | "item" | "pinned"
    condition = null, // Optional, only for item actions
    dataset = {},
    cb,
  }) {
    if (typeof (dataset) === 'function') {
      cb = dataset;
      dataset = null;
    }

    if (scope === 'bulk') {
      className = 'table-column-bulk-action ' + className;
    }

    this.actions.push({scope, name, label, className, icon, condition, cb});

    if (scope === 'item') {
      this.recreateContextMenus();
    } else if (scope === 'pinned') {
      this.recreateQuickRowActionButtons();
    } else {
      this.recreateActionElements();
    }
  }

  findOrCreateDragHandle(parent, header = false) {
    const existingDragColumn = parent.getElementsByClassName('table-column-drag');
    if (existingDragColumn.length > 0) {
      return existingDragColumn[0];
    }

    const domElement = document.createElement(header ? 'th' : 'td');
    domElement.className = 'table-column table-column-drag';

    if (header === false) {
      const iconDomElement = document.createElement('i');
      iconDomElement.className = 'fa-solid fa-grip-vertical';

      domElement.appendChild(iconDomElement);
    }

    parent.appendChild(domElement);

    return domElement;
  }

  findOrCreateCheckboxControl(parent, header = false) {
    const existingCheckboxColumn = parent.getElementsByClassName('table-column-check');
    if (existingCheckboxColumn.length > 0) {
      return existingCheckboxColumn[0];
    }

    const domElement = document.createElement(header ? 'th' : 'td');
    domElement.className = 'table-column table-column-check';

    const labelDomElement = document.createElement('label');
    labelDomElement.className = 'checkbox';

    const inputDomElement = document.createElement('input');
    inputDomElement.type = 'checkbox';
    inputDomElement.className = 'checkbox-control';

    const spanDomElement = document.createElement('span');
    spanDomElement.className = 'checkbox-substitute';

    labelDomElement.appendChild(inputDomElement);
    labelDomElement.appendChild(spanDomElement);

    domElement.appendChild(labelDomElement);
    parent.appendChild(domElement);

    return domElement;
  }

  findOrCreateActionsHeaderColumn(parent, header = false) {
    const existingActionsColumn = parent.getElementsByClassName('table-column-actions');

    let domElement = null;
    if (existingActionsColumn.length > 0) {
      domElement = existingActionsColumn[0];
    } else {
      domElement = document.createElement('th');
      domElement.className = 'table-column table-column-actions';
    }

    parent.appendChild(domElement);

    return domElement;
  }

  createActionsContextMenuElement(data, pk) {
    const contextMenuDomElement = document.createElement('div');
    contextMenuDomElement.className = 'context-menu';
    contextMenuDomElement.id = 'context-menu-' + pk;

    const menuItemWrapperDomElement = document.createElement('div');
    menuItemWrapperDomElement.className = 'menu-item-wrapper';

    for (let i = 0; i < this.actions.length; i++) {
      const action = this.actions[i];

      if (action.scope !== 'item') {
        continue;
      }

      let conditionIsTrue = true;
      if (typeof (action.condition) === 'function') {
        conditionIsTrue = action.condition(data);
      }

      if (conditionIsTrue === false) {
        continue;
      }

      const actionDomElement = document.createElement('a');
      actionDomElement.className = 'menu-item ' + (action.className || '');
      actionDomElement.href = 'javascript://';
      if (!!action.dataset) {
        Object.assign(actionDomElement.dataset, action.dataset);
      }

      const actionIconDomElement = document.createElement('i');

      if (typeof (action.icon) === 'function') {
        actionIconDomElement.className = action.icon(data);
      } else {
        actionIconDomElement.className = action.icon;
      }

      actionDomElement.appendChild(actionIconDomElement);
      actionDomElement.appendChild(document.createTextNode(action.label));

      if (typeof (action.cb) === 'function') {
        actionDomElement.addEventListener('click', function(e) {
          e.preventDefault();
          action.cb(e);
        });
      }

      menuItemWrapperDomElement.appendChild(actionDomElement);
    }

    contextMenuDomElement.appendChild(menuItemWrapperDomElement);

    return contextMenuDomElement;
  }

  createQuickActionElement(data, label, icon, className, dataset, cb) {
    const quickRowActionButtonDomElement = document.createElement('a');
    quickRowActionButtonDomElement.className = 'quick-action-button ' + className;
    quickRowActionButtonDomElement.title = label;
    quickRowActionButtonDomElement.href = 'javascript://';

    if (!!dataset) {
      Object.assign(quickRowActionButtonDomElement.dataset, dataset);
    }

    const quickRowActionIconDomElement = document.createElement('i');

    if (typeof (icon) === 'function') {
      quickRowActionIconDomElement.className = icon(data);
    } else {
      quickRowActionIconDomElement.className = icon;
    }

    quickRowActionButtonDomElement.appendChild(quickRowActionIconDomElement);

    if (typeof (cb) === 'function') {
      quickRowActionButtonDomElement.addEventListener('click', function(e) {
        e.preventDefault();
        cb(e);
      });
    }

    return quickRowActionButtonDomElement;
  }

  createQuickActionElements(data, referenceNode) {
    for (let j = 0; j < this.actions.length; j++) {
      const action = this.actions[j];
      if (action.scope !== 'pinned') {
        continue;
      }

      let conditionIsTrue = true;
      if (typeof (action.condition) === 'function') {
        conditionIsTrue = action.condition(data);
      }

      if (conditionIsTrue === false) {
        continue;
      }

      const quickRowActionButtonDomElement = this.createQuickActionElement(data, action.label, action.icon, action.className, action.dataset, action.cb);
      referenceNode.parentNode.insertBefore(quickRowActionButtonDomElement, referenceNode);
    }
  }

  createActionButtonAndMenu(data, pk) {
    const contextMenuButtonDomElement = document.createElement('a');
    contextMenuButtonDomElement.className = 'context-menu-button';
    contextMenuButtonDomElement.href = 'javascript://';
    contextMenuButtonDomElement.innerHTML = '<i class="fas fa-ellipsis-v"></i>';
    contextMenuButtonDomElement.dataset.menu = 'context-menu-' + pk;

    const menuDomElement = this.createActionsContextMenuElement(data, pk);
    new ContextMenu(menuDomElement, contextMenuButtonDomElement);
    // this.createQuickActionElements(data, contextMenuButtonDomElement);

    return {contextMenuButtonDomElement, menuDomElement};
  }

  setHeaderAutoFieldsMode(enabled) {
    this.headerAutoFieldsMode = enabled;
  }

  setHiddenFieldPatterns(patterns) {
    this.hiddenFieldPatterns = patterns;
  }

  setHeaderFields(fields) {
    this.setHeaderAutoFieldsMode(false);

    // Map string arrays into the headerFields notation {$name: xxx, $title: xxx}
    fields = fields.map((field) => {
      if (typeof (field) === 'string') {
        return {
          $name: field,
        };
      }

      return field;
    });

    this.headerFields = fields;
    this.recreateHeaderFields();
  }

  setPrimaryKeyFieldName(fieldName) {
    this.primaryKeyFieldName = fieldName;
  }

  setRenderFunction(key, func) {
    this.renderFunctions[key] = func;
  }

  setItems(items) {
    this.bodyElement.innerHTML = '';
    this.itemDomElements = [];

    // Automatically determine header fields based on the data
    if (this.headerAutoFieldsMode === true) {
      this.deriveHeaderFieldsFromData(items);
    }

    // Fill the table with data
    for (let i = 0; i < items.length; i++) {
      const rowData = items[i];

      const rowDomElement = this.createItemElement(rowData);

      this.bodyElement.appendChild(rowDomElement);
      this.itemDomElements.push(rowDomElement);
    }
  }

  deriveHeaderFieldsFromData(items) {
    let tempFieldNames = [];

    for (let i = 0; i < items.length; i++) {
      tempFieldNames = [
        ...new Set([...tempFieldNames, ...Object.keys(items[i])]),
      ];
    }

    this.setHeaderFields(tempFieldNames);
  }

  recreateHeaderFields() {
    if (!this.headerDomElement) {
      return;
    }

    const headerFieldNames = this.headerFields;

    if (this.options.draggable) {
      const dragHeaderDomElement = this.findOrCreateDragHandle(this.headerDomElement, true);
    }

    if (this.options.checkboxes) {
      const checkboxHeaderDomElement = this.findOrCreateCheckboxControl(this.headerDomElement, true);
      checkboxHeaderDomElement.addEventListener('change', this.onHeaderCheckboxChange.bind(this));
      this.headerCheckboxDomElement = checkboxHeaderDomElement.getElementsByClassName('checkbox-control')[0];
    }

    const hashBang = Utils.getHashbangValue(window.location.hash);
    const queryObj = Utils.queryToObject(hashBang);

    const existingHeaderFieldDomElements = Array.from(this.headerDomElement.querySelectorAll('[data-name]'));

    for (let i = 0; i < headerFieldNames.length; i++) {
      const fieldTitle = headerFieldNames[i]['$title'] || headerFieldNames[i]['$name'];
      const fieldName = headerFieldNames[i]['$name'];

      let isHiddenField = false;
      for (let j = 0; j < this.hiddenFieldPatterns.length; j++) {
        if (this.hiddenFieldPatterns[j].exec(fieldName) !== null) {
          isHiddenField = true;
          break;
        }
      }

      if (isHiddenField === true) {
        continue;
      }

      let fieldDomElement = null;
      const tempFieldDomElement = existingHeaderFieldDomElements.find((e) => e.dataset.name === fieldName);

      if (!!tempFieldDomElement) {
        fieldDomElement = tempFieldDomElement;
      } else {
        fieldDomElement = document.createElement('th');
        fieldDomElement.className = 'table-column table-column-sortable';
        if (queryObj['sort'] === fieldName) {
          fieldDomElement.className += ' table-column-sorted-asc';
        } else if (queryObj['sort'] === ('-' + fieldName)) {
          fieldDomElement.className += ' table-column-sorted-desc';
        }

        fieldDomElement.appendChild(document.createTextNode(fieldTitle));
        fieldDomElement.title = fieldTitle;
        fieldDomElement.dataset.name = fieldName;
        fieldDomElement.addEventListener('click',
          this.onHeaderFieldClick.bind(this));
      }

      this.headerDomElement.appendChild(fieldDomElement);
    }

    this.headerActionsDomElement = this.findOrCreateActionsHeaderColumn(this.headerDomElement, true);
    this.recreateActionElements();
  }

  createItemElement(item) {
    throw new Error('Method createItemElement is not implemented');
  }

  bindEvents() {
    if (!!this.headerCheckboxDomElement) {
      this.headerCheckboxDomElement.addEventListener('change',
        this.onHeaderCheckboxChange.bind(this));
    }

    for (let i = 0; i < this.itemDomElements.length; i++) {
      const dragColumnDomElement = this.itemDomElements[i].getElementsByClassName(
        'table-column-drag')[0];

      if (!!dragColumnDomElement) {
        new Draggable(this.itemDomElements[i], {
          movement: 'vertical',
          cloneDraggable: true,
          parent: this.bodyElement,
          dragAnchor: dragColumnDomElement,
        });
      }
    }

    for (let i = 0; i < this.itemDomElements.length; i++) {
      const checkboxControlDomElement = this.itemDomElements[i].getElementsByClassName(
        'checkbox-control')[0];

      if (!!checkboxControlDomElement) {
        checkboxControlDomElement.addEventListener('change',
          this.onCheckedItemsChange.bind(this));
      }
    }

    this.recreateContextMenus();
    this.recreateQuickRowActionButtons();
  }

  recreateContextMenus() {
    for (let i = 0; i < this.actionButtonDomElements.length; i++) {
      const contextMenuButtonDomElement = this.actionButtonDomElements[i].getElementsByClassName(
        'context-menu-button')[0];
      if (!contextMenuButtonDomElement) {
        continue;
      }

      const id = (contextMenuButtonDomElement.dataset.menu || '').substring(13);
      if (!id) {
        continue;
      }

      const menuDomElement = this.actionButtonDomElements[i].getElementsByClassName(
        'context-menu')[0];
      if (!!menuDomElement) {
        const tempContextMenu = ContextMenu.getContextMenu(menuDomElement);
        if (!!tempContextMenu) {
          tempContextMenu.destruct();
        }

        this.actionButtonDomElements[i].removeChild(menuDomElement);
      }

      const newMenuDomElement = this.createActionsContextMenuElement(null, id);
      this.actionButtonDomElements[i].appendChild(newMenuDomElement);

      new ContextMenu(newMenuDomElement, contextMenuButtonDomElement);
    }
  }

  recreateQuickRowActionButtons() {
    for (let i = 0; i < this.actionButtonDomElements.length; i++) {
      const contextMenuButtonDomElement = this.actionButtonDomElements[i].getElementsByClassName(
        'context-menu-button')[0];

      this.createQuickActionElements(contextMenuButtonDomElement);
    }
  }

  onHeaderCheckboxChange(e) {
    for (let i = 0; i < this.itemDomElements.length; i++) {
      const checkboxControlDomElement = this.itemDomElements[i].getElementsByClassName(
        'checkbox-control')[0];
      checkboxControlDomElement.checked = this.headerCheckboxDomElement.checked;
    }

    this.onCheckedItemsChange();
  }

  onHeaderFieldClick(e) {
    e.preventDefault();

    const target = e.target;
    if (target.classList.contains('table-column-sortable') === false) {
      return;
    }

    const hashBang = Utils.getHashbangValue(window.location.hash);
    const queryObj = Utils.queryToObject(hashBang);

    if (queryObj['sort'] === target.dataset.name) {
      queryObj['sort'] = '-' + target.dataset.name;
    } else {
      queryObj['sort'] = target.dataset.name;
    }

    window.location.hash = '!/' + Utils.objectToQuery(queryObj);
  }

  toggleBulkActions(force) {
    this.headerDomElement.classList.toggle('table-bulk-visible', force);
  }

  getCheckedItems() {
    const checkedDomElements = Array.from(this.domElement.querySelectorAll(
      '.table-body .table-row .table-column input[type="checkbox"]:checked'));
    return checkedDomElements.map(
      (element) => element.parentNode.parentNode.parentNode);
  }

  getCheckedIds() {
    const rows = this.getCheckedItems();
    return rows.map((element) => element.dataset.id);
  }

  onCheckedItemsChange() {
    const checkedItems = this.getCheckedItems();
    this.headerCheckboxDomElement.parentNode.classList.remove('checkbox-indeterminate');

    if (checkedItems.length === 0) {
      this.headerCheckboxDomElement.checked = false;

      this.toggleBulkActions(false);
    } else {
      if (checkedItems.length === this.itemDomElements.length) {
        this.headerCheckboxDomElement.checked = true;
      } else {
        this.headerCheckboxDomElement.parentNode.classList.add('checkbox-indeterminate');
      }

      this.toggleBulkActions(true);
    }
  }

  static getInstance(element) {
    if (!CollectionView.instanceMap) {
      return null;
    }

    return CollectionView.instanceMap.get(element);
  }
}

export default CollectionView;
